import React from 'react';

/**
 * Reusable platform architecture visualisation.
 *
 * Renders the three operating columns (Farm Operations / Intelligence / Market)
 * sitting on top of Traceability and feeding Data & Analytics. Pure markup —
 * no data — so it can be dropped on any page and scales from 320px upward.
 *
 * The layout is a vertical stack below `sm` and the three-column diagram from
 * `md` up, so there is never any horizontal overflow.
 */
const PlatformDiagram: React.FC<{ className?: string }> = ({ className = '' }) => {
  const columns: Array<{ title: string; items: string[]; tone: string }> = [
    {
      title: 'Farm Operations',
      tone: 'border-[#14532d]/40 bg-[#14532d]/10',
      items: ['Farmers', 'Fields', 'Crops & Trees', 'Activities', 'Scouting', 'Inputs'],
    },
    {
      title: 'Intelligence',
      tone: 'border-[#d4af37]/45 bg-[#d4af37]/10',
      items: ['AI Advisory', 'Weather', 'Satellite & Soil', 'Risk', 'Recommendations', 'Human Review'],
    },
    {
      title: 'Market',
      tone: 'border-white/25 bg-white/[0.07]',
      items: ['Buyers', 'RFQ', 'Contracts', 'Orders', 'Warehouse', 'Logistics'],
    },
  ];

  return (
    <div className={`rounded-2xl border border-white/10 bg-[#06150e] p-5 sm:p-7 ${className}`}>
      <p className="text-center text-xs font-black uppercase tracking-[0.4em] text-[#f3d978]">eSanza</p>
      <div className="mx-auto mt-3 h-5 w-px bg-gradient-to-b from-[#d4af37] to-white/30" aria-hidden="true" />

      <div className="grid gap-3 md:grid-cols-3">
        {columns.map((column) => (
          <div key={column.title} className={`rounded-xl border p-4 ${column.tone}`}>
            <h3 className="text-center text-[11px] font-black uppercase tracking-[0.18em] text-white">
              {column.title}
            </h3>
            <ul className="mt-3 grid gap-1.5">
              {column.items.map((item) => (
                <li
                  key={item}
                  className="rounded-lg border border-white/10 bg-black/25 px-3 py-1.5 text-center text-xs font-semibold text-white/75"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mx-auto mt-3 h-5 w-px bg-gradient-to-t from-[#d4af37] to-white/30" aria-hidden="true" />

      <div className="rounded-xl border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-3 text-center text-xs font-black uppercase tracking-[0.24em] text-[#f3d978]">
        Traceability
      </div>

      <div className="mx-auto mt-3 h-5 w-px bg-gradient-to-t from-[#d4af37] to-white/30" aria-hidden="true" />

      <div className="rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-center text-xs font-black uppercase tracking-[0.24em] text-white/80">
        Data &amp; Analytics
      </div>

      <p className="mt-5 text-center text-xs leading-5 text-white/45">
        Field data → Intelligence → Action → Evidence → Traceability → Supply → Commerce → Logistics → Analytics
      </p>
    </div>
  );
};

export default PlatformDiagram;
